<script>
import { Button } from '@/packages/ui/components';
import Popup from '@/components/Popup.vue';
import { mountSurveyPartialAnswers, getEmailAnswer, updateAnswers } from '@/utils/surveyWeb';
import { mapActions } from 'vuex';
import LanguageChooser from '../Components/LanguageChooser.vue';
import SurveyQuestion from '../Components/SurveyQuestion.vue';
import SurveyAnswered from './SurveyAnswered.vue';
import SurveyMessage from './SurveyMessage.vue';

export default {
  name: 'Survey',
  components: {
    Button,
    Popup,
    SurveyQuestion,
    SurveyAnswered,
    LanguageChooser,
    SurveyMessage,
  },
  props: {
    isPreview: {
      type: Boolean,
      default: false,
    },
    previewSurvey: {
      type: Object,
      default: () => {},
    },
    messageItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      survey: {},
      answers: [],
      validationErrors: [],
      shoudShowPopup: false,
      language: '',
      time: null,
      interactionUUID: null,
      showThanksMessage: false,
      responseTimeTotal: 0,
      customer: null,
      thanksMessageItems: [],
      expiredMessageItems: [],
      buttonDisabled: true,
      navigation: [0],
      openAnswers: [],
      errorComments: [],
    };
  },
  computed: {
    currentIndex() {
      return this.navigation[this.navigation.length - 1];
    },
    currentItem() {
      return this.survey.items.find((i, index) => index === this.currentIndex) || null;
    },
    currentAnswer() {
      return this.getCurrentAnswer(this.currentIndex);
    },
    token() {
      if (this.isPreview) {
        return 'preview';
      }

      return this.$route.params.token;
    },
    jumpRuleAction() {
      // eslint-disable-next-line camelcase
      const { response, option_other, option_other_jump_rule } = this.currentAnswer.answer;

      // eslint-disable-next-line camelcase
      return option_other && option_other_jump_rule
        ? option_other_jump_rule.action : response[0].jump_rules.action;
    },
    jumpRuleValue() {
      // eslint-disable-next-line camelcase
      const { response, option_other, option_other_jump_rule } = this.currentAnswer.answer;

      // eslint-disable-next-line camelcase
      return option_other && option_other_jump_rule
        ? option_other_jump_rule.value : response[0].jump_rules.value;
    },
    isOpen() {
      return this.currentItem.type === this.$consts.QUESTION_TYPE.OPEN;
    },
    disableNextButton() {
      const staticQuestionTypes = [
        this.$consts.QUESTION_TYPE.LABEL,
        this.$consts.QUESTION_TYPE.IMAGE,
      ];
      if (staticQuestionTypes.includes(this.currentItem.type)) {
        return false;
      }
      if (this.currentItem.is_obligatory) {
        if (this.currentAnswer === null) {
          return true;
        }
        if (this.isOpen && this.openAnswers[0].value.trim() === '') {
          return true;
        }
      }

      return false;
    },
    messageObligatory() {
      return this.$i18n.messages[this.language].message_obligatory;
    },
  },
  created() {
    this.clearTimeToRefreshToken();
    this.getSurveyData();
  },
  methods: {
    ...mapActions({
      toggleLoading: 'ui/toggleLoading',
      getSurveyWeb: 'survey/getSurveyWeb',
      savePartialResponses: 'survey/savePartialResponses',
      saveCompleteResponses: 'survey/saveCompleteResponses',
      clearTimeToRefreshToken: 'auth/clearTimeToRefreshToken',
    }),
    async getSurveyData() {
      try {
        const interactionUUID = this.$route.query.interaction_uuid || null;
        let survey = null;
        let messageItems = null;
        if (this.isPreview) {
          survey = this.previewSurvey;
          messageItems = this.messageItems;
        } else {
          const response = await
          this.getSurveyWeb({ token: this.$route.params.token, interactionUUID });

          survey = response.data.data.survey;
          this.interactionUUID = response.data.data.interaction_uuid;
          this.responseTimeTotal = response.data.data.response_time || 0;
          this.customer = response.data.data.customer;
          messageItems = response.data.data.message_items;
        }
        if (messageItems) {
          this.getMessageItems(messageItems);
        }
        this.survey = survey;

        if (this.survey.status === this.$consts.SURVEY_STATUS.FINISHED) {
          this.time = new Date().getTime();

          this.updateAnswers();
        }

        // Get survey language
        const browserLang = this.setBrowserLanguage();
        if (this.survey.lang.includes(browserLang)) {
          this.language = browserLang;
        } else {
          this.language = this.survey.default_lang;
        }
      } catch (e) {
        this.survey = null;
      }
    },
    /**
     * Separate the list of message items in ending message and expired
     */
    getMessageItems(messageItems) {
      messageItems.forEach((message) => {
        if (message.template_type === this.$consts.TEMPLATE_TYPES.SURVEY_COMPLETED) {
          this.thanksMessageItems.push(message);
        } else if (message.template_type === this.$consts.TEMPLATE_TYPES.SURVEY_EXPIRED) {
          this.expiredMessageItems.push(message);
        }
      });
    },
    selectValue(item) {
      const answerIndex = this.answers.findIndex((answer) => answer.uuid === item.uuid);
      if (item.answer.comment) {
        // eslint-disable-next-line no-param-reassign
        item.answer.comment = item.answer.comment.trim();
      }
      if (answerIndex === -1) {
        this.answers = this.answers.concat(item);
      } else {
        const newAnswers = this.answers.filter((i, index) => index !== answerIndex);
        this.answers = newAnswers.concat(item);
      }
      if (this.validationErrors.includes(item.uuid)) {
        this.validationErrors = this.validationErrors.filter((e) => e !== item.uuid);
      }
      if (this.errorComments.includes(item.uuid)) {
        this.errorComments = this.errorComments.filter((e) => e !== item.uuid);
      }
      this.enableButtonSubmit();
      this.savePartial();
    },
    enableButtonSubmit() {
      this.buttonDisabled = true;
      for (let i = 0; i < this.answers.length; i += 1) {
        const answer = this.answers[i];
        if (answer.type === this.$consts.QUESTION_TYPE.OPEN) {
          if (answer.answer.comment) {
            this.buttonDisabled = false;
            return;
          }
        } else
        if (answer.answer.response?.length !== 0 || answer.answer.option_other?.length !== 0) {
          this.buttonDisabled = false;
          return;
        }
      }
    },
    /**
     * Get the partial responses current_response and current_comment and mount the
     * answers array.
     * And
     * Check the selected email button for the first question
     */
    updateAnswers() {
      this.answers = mountSurveyPartialAnswers(this.survey);
      const currentAnswer = getEmailAnswer(this.$route.query.first_answer, this.survey);

      if (currentAnswer) {
        this.answers = updateAnswers(this.answers, currentAnswer);
      }
      this.enableButtonSubmit();
    },
    async handleNextItem() {
      this.validateItem(this.currentItem);
      if (this.shoudShowPopup) {
        return;
      }
      let next = this.currentIndex + 1;
      if (this.currentItem.jump_rules_enabled && this.currentAnswer) {
        if (this.currentItem.type === this.$consts.QUESTION_TYPE.MULTIPLE_CHOICE) {
          if (this.jumpRuleAction === this.$consts.JUMP_ACTIONS.SPECIFIC_QUESTION) {
            next = this.jumpRuleValue;
          } else if (this.jumpRuleAction === this.$consts.JUMP_ACTIONS.FINISH_SURVEY) {
            this.saveComplete();
            return;
          }
        } else {
          const { context } = this.currentAnswer.answer;

          const { action } = this.currentItem.jump_rules[context];
          if (action === this.$consts.JUMP_ACTIONS.SPECIFIC_QUESTION) {
            next = this.currentItem.jump_rules[context].value;
          } else if (action === this.$consts.JUMP_ACTIONS.FINISH_SURVEY) {
            this.saveComplete();
            return;
          }
        }
      }

      if (this.currentIndex !== next && this.survey.items[next]) {
        await this.savePartial();
        this.navigation = this.navigation.concat(next);
      }
    },
    handlePreviousItem() {
      if (this.navigation.length > 0) {
        this.navigation = this.navigation.filter((i) => i !== this.currentIndex);
      }
    },
    validate() {
      this.survey.items.forEach((question) => this.validateItem(question));

      if (this.shoudShowPopup) {
        return false;
      }
      return true;
    },
    validateItem(questionItem) {
      if (questionItem.is_obligatory) {
        let answer;

        if (questionItem.type === this.$consts.QUESTION_TYPE.OPEN) {
          const openAnswer = this.openAnswers.find((internalAnswer) => (internalAnswer.uuid === questionItem.item_uuid));
          if (!openAnswer) {
            answer = null;
          } else {
            answer = {
              type: questionItem.type,
              answer: {
                comment: openAnswer.value,
              },
            };
          }
        } else {
          answer = this.answers.find((internalAnswer) => (internalAnswer.uuid === questionItem.item_uuid));
        }
        if (answer === undefined || answer === null || !this.validateQuestionByType(answer)) {
          this.validationErrors = this.validationErrors.concat(questionItem.item_uuid);
          this.shoudShowPopup = true;
        }
      }
      if (this.validateObligatoryCommentsNps(questionItem)) {
        this.errorComments = this.errorComments.concat(questionItem.item.uuid);
        this.shoudShowPopup = true;
      }
    },
    validateObligatoryCommentsNps(questionItem) {
      if (questionItem.type === this.$consts.QUESTION_TYPE.NPS_RELATIONAL || this.$consts.QUESTION_TYPE.NPS_TRANSACTIONAL) {
        const answer = this.answers.find((internalAnswer) => (internalAnswer.uuid === questionItem.item_uuid));

        if (answer === undefined) {
          return false;
        }

        if (questionItem.comments_enabled && ((questionItem.is_obligatory_detractor && answer.answer.context === this.$consts.JUMP_LOGIC_PATHS.DETRACTOR && !answer.answer.comment)
          || (questionItem.is_obligatory_neuter && answer.answer.context === this.$consts.JUMP_LOGIC_PATHS.PASSIVE && !answer.answer.comment)
          || (questionItem.is_obligatory_promotor && answer.answer.context === this.$consts.JUMP_LOGIC_PATHS.PROMOTER && !answer.answer.comment)
        )) {
          return true;
        }
        return false;
      }
      return false;
    },
    validateQuestionByType(answer) {
      if (answer.type === this.$consts.QUESTION_TYPE.OPEN) {
        if (answer.answer.comment?.length === 0) {
          return false;
        }
        return true;
      }
      if (answer.type === this.$consts.QUESTION_TYPE.MULTIPLE_CHOICE
        && answer.answer.response?.length === 0) {
        if (answer.answer.option_other?.length === 0) {
          return false;
        }
        return true;
      }
      if (answer.answer.response?.length === 0) {
        return false;
      }
      return true;
    },
    async savePartial() {
      const newTime = new Date().getTime();
      const responseTime = newTime - this.time + this.responseTimeTotal;

      const data = this.mountSurveyObject(this.$consts.INTERACTION_STATUS.PARTIAL, responseTime);
      if (!this.isPreview) {
        try {
          await this.savePartialResponses(data);
        } catch (error) {
          this.getErrorMessage(error);
        }
      }
    },
    async saveComplete() {
      if (this.isPreview) {
        return;
      }
      if (!this.survey.jump_rules_enabled && !this.validate()) {
        return;
      } if (this.survey.jump_rules_enabled) {
        this.validateItem(this.currentItem);
        if (this.shoudShowPopup) {
          return;
        }
      }

      this.toggleLoading(true);
      setTimeout(async () => {
        try {
          const newTime = new Date().getTime();
          const responseTime = newTime - this.time + this.responseTimeTotal;
          const data = this.mountSurveyObject(this.$consts.INTERACTION_STATUS.DONE, responseTime);
          await this.saveCompleteResponses(data);
          this.showThanksMessage = true;
        } catch (error) {
          this.getErrorMessage(error);
        } finally {
          this.toggleLoading(false);
        }
      }, 4000);
    },
    getErrorMessage(error) {
      const errorResponse = error.response || {};
      const errorData = errorResponse.data || {};
      const message = errorData.message || '';
      if (message && message.includes('expired')) {
        this.survey.status = this.$consts.SURVEY_STATUS.EXPIRED;
      }
    },
    mountSurveyObject(status, responseTime) {
      return {
        token: this.token,
        survey: {
          questions: this.answers,
          response_time: responseTime,
          status,
          interaction_uuid: this.interactionUUID,
          language: this.language,
        },
      };
    },
    getCurrentAnswer(questionIndex) {
      const currentUuid = this.survey.items[questionIndex].item_uuid;
      return this.answers.find((a) => a.uuid === currentUuid) || null;
    },
    setOpenAnswer(item) {
      const answerIndex = this.openAnswers.findIndex((answer) => answer.uuid === item.uuid);
      if (answerIndex === -1) {
        this.openAnswers = this.openAnswers.concat(item);
      } else {
        const newAnswers = this.openAnswers.filter((i, index) => index !== answerIndex);
        this.openAnswers = newAnswers.concat(item);
      }
    },
  },
  i18n: {
    messages: {
      en_US: {
        message_obligatory: 'Please provide an answer for this question.',
      },
      es_ES: {
        message_obligatory: 'Proporcione una respuesta para esta pregunta.',
      },
      pt_BR: {
        message_obligatory: 'Por favor, forneça uma resposta para esta pergunta. ',
      },
    },
  },
};
</script>

<template>
  <div class="cxm-Survey">
    <div v-if="survey">
      <SurveyAnswered
        v-if="survey.status === $consts.SURVEY_STATUS.ANSWERED"
        :survey="survey"
      />
      <SurveyMessage
        v-else-if="survey.status === $consts.SURVEY_STATUS.EXPIRED"
        :message-items="expiredMessageItems"
        :customer="customer"
        :lang="language"
        class="cxm-Survey-Message"
      />
      <SurveyMessage
        v-else-if="showThanksMessage"
        :message-items="thanksMessageItems"
        :customer="customer"
        :lang="language"
        class="cxm-Survey-Message"
      />
      <div
        v-else
        class="cxm-Survey-FormWrapper"
        :class="{'-Preview': isPreview }"
      >
        <LanguageChooser
          v-model="language"
          :default-lang="survey.default_lang"
          :other-lang="survey.lang"
          :second-color="survey.secondary_color"
        />
        <form ref="form">
          <div
            v-if="survey.jump_rules_enabled"
            class="cxm-Survey-Question"
          >
            <div
              v-if="validationErrors.includes(currentItem.item_uuid)"
              class="cxm-Survey-Empty"
            >
              <img
                src="survey/error_icon.svg"
                style="margin: -4px"
              >
              <span style="margin: 10px">{{ messageObligatory }}</span>
            </div>
            <SurveyQuestion
              :key="currentItem.uuid"
              :question-data="currentItem"
              :color="survey.primary_color"
              :lang="language"
              :on-select-answer="(item) => selectValue(item)"
              :status="survey.status"
              :current-answer="currentAnswer"
              :is-edit="isPreview"
              :error="errorComments.includes(currentItem.item_uuid) ? true : false"
              @openAnswer="setOpenAnswer"
            />
          </div>
          <div
            v-for="(question, index) in survey.items"
            v-else
            :key="question.uuid"
            class="cxm-Survey-Question"
          >
            <SurveyQuestion
              :question-data="question"
              :color="survey.primary_color"
              :lang="language"
              :on-select-answer="(item) => selectValue(item)"
              :status="survey.status"
              :current-answer="getCurrentAnswer(index)"
              :is-edit="isPreview"
              :error="errorComments.includes(question.item_uuid) ? true : false"
              @openAnswer="setOpenAnswer"
            />
            <div
              v-if="validationErrors.includes(question.item_uuid)"
              class="cxm-Survey-Empty"
            >
              <img
                src="survey/error_icon.svg"
                style="margin: -4px"
              >
              <span style="margin: 10px">{{ messageObligatory }}</span>
            </div>
          </div>
          <div class="cxm-Survey-NavigationContainer">
            <Button
              v-if="survey.jump_rules_enabled && currentIndex !== survey.items.length - 1"
              :label="$t('survey.web.button_next')"
              :handle-click="() => handleNextItem()"
              :disabled="disableNextButton"
              :custom-color="survey.secondary_color"
            />
            <Button
              v-else
              :label="$t('survey.web.button_send')"
              :disabled="buttonDisabled"
              :handle-click="saveComplete"
              :custom-color="survey.secondary_color"
            />
          </div>
          <div style="cxm-Survey-LogoContainer">
            <a
              href="https://track.co/"
              target="_blank"
            ><img
              src="logo.png"
              style="width: 125px"
            ></a>
          </div>
        </form>
        <Popup
          v-model="shoudShowPopup"
          type="alert"
          :title="$t('survey.web.popup.title')"
          :content="$t('survey.web.popup.content')"
          single-button
          :button-confirm-label="$t('survey.web.popup.button_ok')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cxm-Survey {
  &-Message {
    padding-top: var(--track-spacing-xl);
  }

  &-Question {
    margin-top: var(--track-spacing-lg);
    @media screen and (max-width: 1024px) {
      margin-top: var(--track-spacing-md);
    }
  }

  &-FormWrapper {
    max-width: 700px;
    margin: 0 auto;
    margin-top: var(--track-spacing-xl);
    text-align: left;
    padding: var(--track-spacing-sm);

    &.-Preview {
      margin-top: 0px;
    }
    @media screen and (max-width: 1024px) {
      margin-top: var(--track-spacing-md);
      padding: var(--track-spacing-md);
    }
  }

  &-Empty {
    color: #ea575f;
    font-size: var(--track-font-size-sm);
    margin-bottom: var(--track-spacing-md);
    margin-top: var(--track-spacing-md);
    text-align: center;
  }

  &-NavigationContainer {
    display: flex;
    justify-content: center;
    margin-top: var(--track-spacing-lg);
  }

  &-LogoContainer {
    width: 100%;
    display: flex;
    justify-content: center;
  }
}

@media (prefers-color-scheme: dark) {
  body,
  html {
    background-color: white;
  }
}
</style>
